import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { WorkspaceState, WorkspaceActions, ProjectContext, ColumnFilters, QuickFilters, LocationFilter, PortalType, SpatialOverlay, AnalyzeSession, ViewType, ViolationPoleData } from './types';
import { PORTAL_DESCRIPTORS } from './types';
import type { GridSortModel, GridFilterModel, GridPaginationModel, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';

const IDLE_ANALYZE: AnalyzeSession = {
  status: 'idle',
  scopeIds: [],
  scopeSource: null,
  scopeProjectName: null,
  inspectorOpen: false,
  inspectorHeight: 280,
};

// ── Stability helpers ────────────────────────────────────────────────────────
// Return the SAME reference when the page is already 0, so the DataGrid's
// onPaginationModelChange callback does not fire again and start an update loop.
function withPageReset(current: GridPaginationModel): GridPaginationModel {
  return current.page === 0 ? current : { ...current, page: 0 };
}

// Cheap serialisable equality — used to guard sort/filter model setters so that
// the MUI DataGrid controlled-state callbacks don't spin into an update loop when
// they call onSortModelChange / onFilterModelChange with new-reference-but-same-
// content objects on every render.
function jsonEq(a: unknown, b: unknown): boolean {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
}

export const useWorkspace = create<WorkspaceState & WorkspaceActions>()(
  persist(
    (set, get) => ({
      // ── Context ──
      // SINGLE OWNER: projectContext is written only by ProjectsPortal via showProjectOnMap().
      // No other portal or store action should set this field directly.
      // For analysis scope, add a dedicated lane (e.g. analysisScopeIds) — never reuse this.
      projectContext: null,
      setProjectContext: (ctx: ProjectContext) =>
        set((s) => ({ projectContext: ctx, paginationModel: withPageReset(s.paginationModel) })),
      clearProjectContext: () =>
        set((s) => ({
          projectContext: null,
          // Clear both selection lanes when exiting a project scope so the map
          // returns to a clean state.
          selectedIds: s.previewSource === 'scope' ? [] : s.selectedIds,
          previewIds: s.previewSource === 'scope' ? [] : s.previewIds,
          previewSource: 'user',
          paginationModel: withPageReset(s.paginationModel),
        })),

      // ── Search ──
      searchQuery: '',
      setSearchQuery: (q: string) =>
        set((s) => ({
          searchQuery: q,
          paginationModel: withPageReset(s.paginationModel),
          // Auto-clear MR pole overlay when user starts searching
          ...(s.spatialOverlay?.source === 'mr' ? { spatialOverlay: null } : {}),
        })),
      clearSearch: () => set({ searchQuery: '' }),

      // ── Location Filter ──
      locationFilter: null,
      setLocationFilter: (loc: LocationFilter) =>
        set((s) => ({ locationFilter: loc, paginationModel: withPageReset(s.paginationModel) })),
      clearLocationFilter: () => set({ locationFilter: null }),

      // ── Filters ──
      columnFilters: {},
      setColumnFilters: (f: ColumnFilters) =>
        set((s) => ({ columnFilters: f, paginationModel: withPageReset(s.paginationModel) })),

      filterModel: { items: [] },
      setFilterModel: (m: GridFilterModel) => {
        const s = get();
        // No-op if content is identical — prevents DataGrid controlled-state loop
        if (jsonEq(m, s.filterModel)) return;
        set({ filterModel: m, paginationModel: withPageReset(s.paginationModel) });
      },

      quickFilters: { myJobs: false, readyForPickup: false },
      setQuickFilters: (qf: Partial<QuickFilters>) =>
        set((s) => ({
          quickFilters: { ...s.quickFilters, ...qf },
          paginationModel: withPageReset(s.paginationModel),
        })),

      // ── Sort + Pagination ──
      sortModel: [{ field: 'last_user_edit_at', sort: 'desc' }],
      setSortModel: (m: GridSortModel) => {
        const arr = Array.isArray(m) ? m : [];
        // No-op if content is identical — prevents DataGrid controlled-state loop
        if (jsonEq(arr, get().sortModel)) return;
        set({ sortModel: arr });
      },

      paginationModel: { page: 0, pageSize: 50 },
      setPaginationModel: (m: GridPaginationModel) => {
        const cur = get().paginationModel;
        // No-op if values are identical — prevents DataGrid controlled-state loop
        if (cur.page === m.page && cur.pageSize === m.pageSize) return;
        set({ paginationModel: m });
      },

      // ── Column Visibility + Presets ──
      columnVisibilityModel: null,
      setColumnVisibilityModel: (m: GridColumnVisibilityModel) => {
        if (jsonEq(m, get().columnVisibilityModel)) return;
        set({ columnVisibilityModel: m });
      },
      gridDensity: 'compact',
      setGridDensity: (d) => {
        if (d === get().gridDensity) return;
        set({ gridDensity: d });
      },
      gridPreset: null,
      setGridPreset: (key: string | null) => {
        if (key === get().gridPreset) return;
        set({ gridPreset: key });
      },

      // ── Selection ──
      selectedIds: [],
      previewIds: [],
      previewSource: 'user',
      focusedId: null,
      setSelectedIds: (ids: string[]) => {
        const next = [...new Set(ids)];
        const cur = get().selectedIds;
        if (cur.length === next.length && cur.every((v, i) => v === next[i])) return;
        // Auto-clear MR pole overlay when user selects jobs (queue table, map click)
        const overlay = get().spatialOverlay;
        const updates: Partial<WorkspaceState> = { selectedIds: next };
        if (overlay?.source === 'mr') updates.spatialOverlay = null;
        set(updates);
      },
      setPreviewIds: (ids: string[], source: 'user' | 'scope' = 'user') => {
        const next = ids.map(String);
        const cur = get().previewIds;
        // Skip update when content and source are identical — prevents DataGrid callback loops
        if (
          get().previewSource === source &&
          cur.length === next.length &&
          cur.every((v, i) => v === next[i])
        ) return;
        set({ previewIds: next, previewSource: source });
      },
      togglePreviewId: (id: string) => {
        const s = String(id);
        const cur = get().previewIds;
        set({ previewIds: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s], previewSource: 'user' });
      },
      confirmPreview: () => {
        const { previewIds, previewSource } = get();
        // Guard: scope-source previewIds are project highlights, not user checkbox selections.
        // Confirming them would move hundreds of project jobs into selectedIds and trigger
        // pole loads. Only confirm user-driven previews.
        if (previewIds.length > 0 && previewSource === 'user') {
          set({ selectedIds: [...new Set(previewIds)], previewIds: [], previewSource: 'user' });
        }
      },
      setFocusedId: (id: string | null) => set({ focusedId: id ? String(id) : null }),

      // Atomic "Show on Map" state update — semantic category: context/scope.
      // Sets projectContext (scope chip) and selectedIds (table checkboxes + map zoom)
      // in a single transaction. Does NOT call fitToJobs — callers are responsible for the
      // zoom command so that status colors (computed outside the store) are forwarded.
      showProjectOnMap: async (context: ProjectContext) => {
        const s = get();
        const katapultIds = context.katapultIds ?? [];
        // Lazy import breaks circular init — both stores re-exported from @/stores barrel
        const { useJobIdentity } = await import('./jobIdentityStore');
        const rowIds = katapultIds.map(
          (kid) => useJobIdentity.getState().resolve(kid)?.id ?? kid
        );
        const uniqueIds = [...new Set(rowIds)];
        // Set both selectedIds (drives Esri highlight + zoom) and previewIds
        // (drives Deck.gl yellow polygons). The jobs table will re-filter to
        // only this project's jobs and then replace selectedIds with the full
        // resolved set after load completes.
        set({
          projectContext: context,
          selectedIds: uniqueIds,
          previewIds: uniqueIds,
          previewSource: 'scope',
          paginationModel: withPageReset(s.paginationModel),
        });
      },

      // ── Spatial Overlay ──
      // Isolated lane for portal / tool spatial output. NOT persisted.
      // Cleared at Escape ×2 alongside selectedIds — both are map working-session artifacts.
      spatialOverlay: null,
      setSpatialOverlay: (overlay: SpatialOverlay) => set({ spatialOverlay: overlay }),
      clearSpatialOverlay: () => set({ spatialOverlay: null }),

      // ── View ──
      activeView: 'default' as ViewType,
      setActiveView: (view: ViewType) => set({ activeView: view, designFilter: null }),
      designFilter: null as string | null,
      setDesignFilter: (f: string | null) => set({ designFilter: f }),

      // ── Panel / Layout ──
      activePortal: null,
      portalExpanded: false,
      mapEngine: 'esri',
      setActivePortal: (p: PortalType | null) => {
        const updates: any = { activePortal: p, portalExpanded: false };
        // Exit DeepRoads view when opening a side panel portal
        if (p && get().activeView === 'deeproads') {
          updates.activeView = 'default';
        }
        set(updates);
      },
      setPortalExpanded: (v: boolean) => {
        const s = get();
        if (v && !s.canExpandPortal()) return;
        set({ portalExpanded: v });
      },
      setMapEngine: (e: 'deepsync' | 'esri') => set({ mapEngine: e }),
      canExpandPortal: () => {
        const { activePortal, analyzeSession } = get();
        if (analyzeSession.status !== 'idle') return false;
        if (activePortal && PORTAL_DESCRIPTORS[activePortal]?.requiresMapVisible) return false;
        return true;
      },

      // ── Analyze Session (Step 6) ──
      analyzeSession: { ...IDLE_ANALYZE },
      startAnalyze: (scopeIds, scopeSource, projectName) => set({
        analyzeSession: {
          status: 'ready',
          scopeIds,
          scopeSource,
          scopeProjectName: projectName ?? null,
          inspectorOpen: true,
          inspectorHeight: get().analyzeSession.inspectorHeight,
        },
      }),
      closeAnalyze: () => set((s) => ({
        analyzeSession: { ...IDLE_ANALYZE, inspectorHeight: s.analyzeSession.inspectorHeight },
        spatialOverlay: s.spatialOverlay?.source === 'analyze' ? null : s.spatialOverlay,
        violationPoles: [],
        highlightedViolationPoleId: null,
        highlightedConnectionId: null,
      })),
      setAnalyzeInspector: (open) => set((s) => ({
        analyzeSession: { ...s.analyzeSession, inspectorOpen: open },
      })),
      setAnalyzeInspectorHeight: (h) => set((s) => ({
        analyzeSession: { ...s.analyzeSession, inspectorHeight: h },
      })),

      // ── QA Violation Poles (Map Interaction) ──
      // NOT persisted — transient map layer data tied to the active analysis session.
      violationPoles: [],
      highlightedViolationPoleId: null,
      highlightedConnectionId: null,
      setViolationPoles: (poles: ViolationPoleData[]) => set({ violationPoles: poles }),
      setHighlightedViolationPoleId: (id: string | null) => set({ highlightedViolationPoleId: id }),
      setHighlightedConnectionId: (id: string | null) => set({ highlightedConnectionId: id }),

      // ── DeepTrace Target ──
      deepTraceTarget: null,
      openDeepTrace: (jobId: string, poleId: string) => {
        set({ deepTraceTarget: { jobId, poleId }, activePortal: 'deeptrace' as PortalType, portalExpanded: false });
      },
      clearDeepTraceTarget: () => set({ deepTraceTarget: null }),

      // ── Make-Ready Panel ──
      selectedPoleForMR: null,
      selectPoleForMR: (pole: Record<string, any>) => set({ selectedPoleForMR: pole, selectedAnchorForEdit: null, selectedConnectionForEdit: null }),
      clearPoleForMR: () => set({ selectedPoleForMR: null }),

      // ── Anchor Property Panel ──
      selectedAnchorForEdit: null,
      selectAnchorForEdit: (anchor: Record<string, any>) => set({ selectedAnchorForEdit: anchor, selectedPoleForMR: null, selectedConnectionForEdit: null }),
      clearAnchorForEdit: () => set({ selectedAnchorForEdit: null }),

      // ── Connection Property Panel ──
      selectedConnectionForEdit: null,
      selectConnectionForEdit: (conn: Record<string, any>) => set({ selectedConnectionForEdit: conn, selectedPoleForMR: null, selectedAnchorForEdit: null }),
      clearConnectionForEdit: () => set({ selectedConnectionForEdit: null }),

      // ── Layered Reset ──
      // spatialOverlay intentionally NOT cleared at Escape ×1 — overlay is a working-session
      // artifact that should survive portal close and preview clear.
      // analyzeSession persists through Escape ×1 — it is a background spatial session.
      resetFocusAndPreview: () => {
        const updates: any = { focusedId: null, previewIds: [], previewSource: 'user', activePortal: null, portalExpanded: false };
        if (get().activeView === 'deeproads') updates.activeView = 'default';
        set(updates);
      },
      // spatialOverlay cleared at Escape ×2 alongside selectedIds — both represent the active
      // map working session.
      resetSelection: () =>
        set({ selectedIds: [], previewIds: [], previewSource: 'user', focusedId: null, spatialOverlay: null }),
      // Escape ×3 — full workspace reset. analyzeSession returns to idle.
      resetWorkspace: () =>
        set((s) => ({
          projectContext: null,
          searchQuery: '',
          locationFilter: null,
          columnFilters: {},
          filterModel: { items: [] },
          quickFilters: { myJobs: false, readyForPickup: false },
          selectedIds: [],
          previewIds: [],
          previewSource: 'user',
          focusedId: null,
          activePortal: null,
          portalExpanded: false,
          spatialOverlay: null,
          violationPoles: [],
          highlightedViolationPoleId: null,
          highlightedConnectionId: null,
          deepTraceTarget: null,
          selectedPoleForMR: null,
          selectedAnchorForEdit: null,
          selectedConnectionForEdit: null,
          analyzeSession: { ...IDLE_ANALYZE, inspectorHeight: s.analyzeSession.inspectorHeight },
          // sortModel and mapEngine intentionally preserved
        })),
    }),
    {
      name: 'deepsync-workspace',
      version: 3,
      migrate: (persisted: any) => ({
        ...persisted,
        mapEngine: 'esri', // override any stored 'deepsync' value
        activeView: persisted?.activeView ?? 'default',
        activePortal: persisted?.activePortal ?? null,
        portalExpanded: persisted?.portalExpanded ?? false,
      }),
      partialize: (state) => ({
        sortModel: state.sortModel,
        paginationModel: state.paginationModel,
        filterModel: state.filterModel,
        quickFilters: state.quickFilters,
        mapEngine: state.mapEngine,
        columnVisibilityModel: state.columnVisibilityModel,
        gridDensity: state.gridDensity,
        gridPreset: state.gridPreset,
        activeView: state.activeView,
        activePortal: state.activePortal,
        portalExpanded: state.portalExpanded,
      }),
      merge: (persisted: unknown, current: WorkspaceState & WorkspaceActions) => {
        const stored = (persisted ?? {}) as Partial<WorkspaceState>;
        const DEFAULT_SORT: GridSortModel = [{ field: 'last_user_edit_at', sort: 'desc' }];
        // Never allow the name column to be hidden on page load — a persisted
        // name:false (e.g. set during a tree-view session) would make the Job Name
        // column invisible until the user toggled away and back.
        const pCVM = stored.columnVisibilityModel;
        const columnVisibilityModel = pCVM ? { ...pCVM, name: true } : pCVM;
        return {
          ...current,
          ...stored,
          columnVisibilityModel,
          // Always reset sort to last-edited-desc on page load
          sortModel: DEFAULT_SORT,
          // Always clear column filters on page load — stale filters (e.g. a project
          // filter from a previous session) should not survive a fresh page load.
          filterModel: { items: [] },
        } as WorkspaceState & WorkspaceActions;
      },
    },
  ),
);
